import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Kropka przy etykiecie: znacznik zmienionego pola, punkt listy, ślad kroku.
 *
 * Kolor idzie przez `currentColor`, więc ton opisuje jedna klasa, a wypełnienie
 * i pierścień biorą ją same.
 */
const dotVariants = cva('inline-block shrink-0 rounded-full', {
  variants: {
    size: {
      /** Znacznik przy tekście — pozycja tabeli, zmienione pole. */
      sm: 'size-[7px]',
      /** Punkt listy i pasków w panelu bocznym. */
      md: 'size-2',
      /** Nagłówek modala i belka szkicu. */
      lg: 'size-[9px]',
      /** Obręcz koloru kategorii kosztów — barwa jest tu daną, nie ozdobą. */
      xl: 'size-[13px]',
    },
    tone: {
      accent: 'text-accent-600',
      success: 'text-success-300',
      error: 'text-error-300',
      neutral: 'text-neutral-400',
    },
    /** Pierścień zamiast wypełnienia — rzecz otwarta: szkic, krok w biegu. */
    hollow: {
      true: 'border-2 border-current',
      false: 'bg-current',
    },
  },
  compoundVariants: [
    { hollow: true, size: 'xl', class: 'border-[3px] bg-basic-white' },
  ],
  defaultVariants: { size: 'md', tone: 'accent', hollow: false },
});

export type DotTone = NonNullable<VariantProps<typeof dotVariants>['tone']>;

type DotProps = React.ComponentProps<'span'> & VariantProps<typeof dotVariants>;

const Dot = ({ size, tone, hollow, className, ...props }: DotProps) => (
  <span
    // Ozdobnik, chyba że wołający nadał jej znaczenie własną etykietą.
    aria-hidden={props['aria-label'] ? undefined : true}
    className={cn(dotVariants({ size, tone, hollow }), className)}
    {...props}
  />
);

export default Dot;
